// src/components/ProductDetailModal.tsx
import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Package } from 'lucide-react';
import { formatPrice } from '../../utils/utilityFunctions';

interface ProductDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: {
    name: string;
    variant_name?: string;
    price: number;
    product_image: string;
    vendor_name?: string;
    sku?: string;
    images?: string[];  // All images for this variant/product
  };
}

const ProductDetailModal: React.FC<ProductDetailModalProps> = ({ isOpen, onClose, product }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!isOpen) return null;

  const images = product.images && product.images.length > 0 
    ? product.images 
    : [product.product_image || 'https://via.placeholder.com/400x400?text=No+Image'];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div 
      className="fixed inset-0 z-50 overflow-hidden bg-black/80"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="relative bg-white rounded-2xl max-w-md w-full max-h-[90vh] flex flex-col">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute right-3 top-3 z-10 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-700" />
          </button>

          {/* Image Carousel */}
          <div className="relative w-full bg-gray-100 rounded-t-2xl overflow-hidden" style={{ height: '300px' }}>
            <img
              src={images[currentImageIndex]}
              alt={product.name}
              className="w-full h-full object-contain"
              onError={(e) => {
                e.currentTarget.src = 'https://via.placeholder.com/400x400?text=No+Image';
              }}
            />
            
            {images.length > 1 && (
              <>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full">
                  {currentImageIndex + 1} / {images.length}
                </div>
                <button
                  onClick={prevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
          </div>

          {/* Product Details */}
          <div className="p-5 overflow-y-auto flex-1">
            <div className="space-y-2">
              <h3 className="text-lg font-bold text-gray-900">{product.name}</h3>
              
              {product.variant_name && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Variant:</span> {product.variant_name}
                </p>
              )}
              
              {product.vendor_name && (
                <p className="text-sm text-gray-500">
                  <span className="font-medium">Vendor:</span> {product.vendor_name}
                </p>
              )}
              
              {product.sku && (
                <p className="text-sm text-gray-500">
                  <span className="font-medium">SKU:</span> <span className="font-mono">{product.sku}</span>
                </p>
              )}
              
              <p className="text-xl font-bold text-primary-600">{formatPrice(product.price)}</p>
            </div>

            {/* Image Thumbnails */}
            {images.length > 1 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                  All Images
                </p>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {images.map((img, idx) => (
                    <div
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 cursor-pointer transition-colors ${
                        idx === currentImageIndex ? 'border-primary-500' : 'border-transparent hover:border-gray-300'
                      }`}
                    >
                      <img
                        src={img}
                        alt={`${product.name} ${idx + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src = 'https://via.placeholder.com/64x64?text=No+Image';
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailModal;